import React, { useRef, useState, useCallback } from 'react';
import { FONTS } from '../../styles/tokens';

const ACCEPTED_TYPES: ReadonlySet<string> = new Set([
  'image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg', 'image/webp',
]);
const MAX_BYTES = 256 * 1024;

interface IconUploaderProps {
  isDark: boolean;
  colors: { text: string; textSecondary: string; border: string; card: string };
  onChange: (file: File | null) => void;
  /** Existing icon URL, shown when no new file has been chosen. */
  currentUrl?: string;
  /** Replaces the default hint line. */
  hint?: string;
}

/**
 * Small inline uploader for an optional app icon. Validates MIME type +
 * size client-side and shows a live preview. Emits null when the user
 * removes the selected file.
 */
export function IconUploader(props: IconUploaderProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback((candidate: File | undefined) => {
    if (!candidate) return;
    if (!ACCEPTED_TYPES.has(candidate.type)) {
      setError(`Unsupported type: ${candidate.type || 'unknown'}`);
      return;
    }
    if (candidate.size > MAX_BYTES) {
      setError(`Too large: max ${Math.floor(MAX_BYTES / 1024)} KB`);
      return;
    }
    setError(null);
    setFile(candidate);
    setPreviewUrl(URL.createObjectURL(candidate));
    props.onChange(candidate);
  }, [props]);

  const clear = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setError(null);
    props.onChange(null);
  }, [previewUrl, props]);

  // What the tile shows: a freshly picked file wins, then the stored icon.
  const shown = previewUrl ?? props.currentUrl ?? null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
      {shown ? (
        <PreviewTile url={shown} onClear={previewUrl ? clear : undefined} />
      ) : (
        <PickerTile
          isDark={props.isDark}
          borderColor={props.colors.border}
          textSecondary={props.colors.textSecondary}
          onClick={() => inputRef.current?.click()}
        />
      )}
      {/* 32px is favicon and table-row scale. Showing it next to the large tile
          is the cheapest way to tell an operator their wordmark will not survive
          being shrunk, before they save it. */}
      {shown && <SmallPreview url={shown} label="32px" textSecondary={props.colors.textSecondary} />}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ fontFamily: FONTS.body, fontSize: '12px', color: props.colors.textSecondary }}>
          {file
            ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
            : props.hint ?? 'Optional. PNG, SVG, JPG or WebP, max 256 KB.'}
        </div>
        {shown && !previewUrl && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            style={{
              alignSelf: 'flex-start', marginTop: '4px', padding: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: FONTS.body, fontSize: '11px', color: '#01A9ED',
            }}
          >
            Replace
          </button>
        )}
        {error && (
          <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: '#EF4444', marginTop: '2px' }}>{error}</div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/svg+xml,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={e => accept(e.target.files?.[0])}
      />
    </div>
  );
}

/** Clickable empty tile used when no icon is selected. */
function PickerTile(props: {
  isDark: boolean;
  borderColor: string;
  textSecondary: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        width: '56px',
        height: '56px',
        borderRadius: '10px',
        border: `1px dashed ${props.borderColor}`,
        background: props.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        color: props.textSecondary,
        fontSize: '20px',
        cursor: 'pointer',
      }}
      aria-label="Upload icon"
    >
      +
    </button>
  );
}

/** Preview tile shown once an icon is chosen, with a clear button overlay. */
function PreviewTile({ url, onClear }: { url: string; onClear?: () => void }): React.JSX.Element {
  return (
    <div style={{ position: 'relative', width: '56px', height: '56px' }}>
      <img
        src={url}
        alt="App icon preview"
        style={{
          width: '56px', height: '56px',
          borderRadius: '10px', objectFit: 'cover',
          border: '1px solid rgba(255,255,255,0.08)',
          background: '#0b0f15',
        }}
      />
      {onClear && <button
        type="button"
        onClick={onClear}
        aria-label="Remove icon"
        style={{
          position: 'absolute', top: -6, right: -6,
          width: '18px', height: '18px', borderRadius: '50%',
          border: 'none', cursor: 'pointer',
          background: '#1f2937', color: '#FFF', fontSize: '10px',
        }}
      >
        ×
      </button>}
    </div>
  );
}

/** The same mark at 32px, so small-size legibility is visible before saving. */
function SmallPreview({ url, label, textSecondary }: {
  url: string; label: string; textSecondary: string;
}): React.JSX.Element {
  return (
    <div style={{ display: 'grid', placeItems: 'center', gap: '4px' }}>
      <img
        src={url}
        alt=""
        style={{
          width: '32px', height: '32px', objectFit: 'contain',
          borderRadius: '7px', padding: '4px', boxSizing: 'border-box',
          background: '#F2F3F5', border: '1px solid rgba(0,0,0,0.10)',
        }}
      />
      <span style={{ fontFamily: FONTS.body, fontSize: '9px', color: textSecondary }}>{label}</span>
    </div>
  );
}
